// OpenTelemetry browser SDK 初始化（Mini App 端）。
//
// 必须在 main.tsx 中**第一行**被 import：DocumentLoadInstrumentation 需要在
// `window.load` 事件之前注册才能捕获到完整的初始页面加载 trace。
//
// 设计原则：
// 1. 完全条件化：未设 VITE_OTEL_EXPORTER_OTLP_ENDPOINT 即彻底 no-op
// 2. **不在浏览器侧持有 Grafana Cloud auth token**：所有 OTLP 流量都打到
//    apps/otel-proxy（Cloudflare Worker），由它在服务端拼 Basic Auth header
//    再转发到 Grafana Cloud。详见 apps/otel-proxy/wrangler.jsonc。
//    所以这里只配 endpoint（如 https://otel.sabay.uk），**没有 headers**。
// 3. CORS：FetchInstrumentation 把 traceparent 注入到 outgoing fetch，
//    api-server 必须把这条 header 列入 allowedHeaders（已在 src/main.ts CORS 配置），
//    otel-proxy 也必须 allow（已在它的 src/index.ts CORS 配置）
// 4. propagateTraceHeaderCorsUrls：必须列出 api.sabay.* 这种**实际跨域**的目标，
//    否则浏览器拒绝带自定义 header 的 preflight，traceparent 不会发出
// 5. 失败保护：try/catch 包整段，OTel 异常**绝不**让 Mini App 起不来
//
// 关联：plans/observability-full-stack/plan.md § 4 Phase 4.3 / 4.4 / otel-proxy
//      docs/node-react-otel-guide.md § 4
//      apps/api-server/src/main.ts CORS 配置（必须含 traceparent / tracestate）
//      apps/otel-proxy/src/index.ts（auth 在这里注入）

const endpoint = (import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT as string | undefined)?.trim();

if (!endpoint) {
  // 本地 dev / 未配 OTel 的环境，安静退出
  // eslint-disable-next-line no-console
  console.log('[otel-web] VITE_OTEL_EXPORTER_OTLP_ENDPOINT not set — browser OTel disabled');
} else {
  try {
    initOtelWeb(endpoint);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[otel-web] init failed — continuing without telemetry', err);
  }
}

function initOtelWeb(otlpEndpoint: string): void {
  // 这些 import 故意放在条件分支里：未配 OTel 时连 SDK 都不加载，省 bundle 体积
  /* eslint-disable @typescript-eslint/no-require-imports */
  // 浏览器环境用 Vite 的 ESM dynamic import 模式更安全；这里我们用 top-level await 风格
  void (async (): Promise<void> => {
    const [
      { WebTracerProvider, BatchSpanProcessor },
      { OTLPTraceExporter },
      { ZoneContextManager },
      { DocumentLoadInstrumentation },
      { FetchInstrumentation },
      { XMLHttpRequestInstrumentation },
      { UserInteractionInstrumentation },
      { registerInstrumentations },
      { Resource },
      { SemanticResourceAttributes },
    ] = await Promise.all([
      import('@opentelemetry/sdk-trace-web'),
      import('@opentelemetry/exporter-trace-otlp-http'),
      import('@opentelemetry/context-zone'),
      import('@opentelemetry/instrumentation-document-load'),
      import('@opentelemetry/instrumentation-fetch'),
      import('@opentelemetry/instrumentation-xml-http-request'),
      import('@opentelemetry/instrumentation-user-interaction'),
      import('@opentelemetry/instrumentation'),
      import('@opentelemetry/resources'),
      import('@opentelemetry/semantic-conventions'),
    ]);
    /* eslint-enable @typescript-eslint/no-require-imports */

    // ⚠️ 故意不读 VITE_OTEL_EXPORTER_OTLP_HEADERS：
    // Vite 把 VITE_* 全部烘进浏览器 bundle，token 暴露在 F12。
    // 改用 apps/otel-proxy（Cloudflare Worker）做服务端 token 注入。
    const exporter = new OTLPTraceExporter({
      url: `${otlpEndpoint}/v1/traces`,
    });

    const provider = new WebTracerProvider({
      resource: new Resource({
        [SemanticResourceAttributes.SERVICE_NAME]:
          (import.meta.env.VITE_OTEL_SERVICE_NAME as string | undefined) ?? 'sabay-mini-app',
        [SemanticResourceAttributes.SERVICE_VERSION]: '1.0.0',
        'deployment.environment':
          (import.meta.env.MODE as string | undefined) ?? 'development',
      }),
      spanProcessors: [new BatchSpanProcessor(exporter)],
    });

    provider.register({ contextManager: new ZoneContextManager() });

    // 哪些跨域目标允许带 traceparent 头
    // - 默认匹配 api.sabay.* / *.sabay.app / *.sabay.uk 等
    // - 本地 dev：localhost / 127.0.0.1
    const propagateTraceHeaderCorsUrls = [
      /^https:\/\/api\.sabay\.[a-z]+(\/.*)?$/,
      /^https:\/\/[a-z-]+\.sabay\.[a-z]+(\/.*)?$/,
      /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/,
    ];

    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls,
          clearTimingResources: true,
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls,
        }),
        new UserInteractionInstrumentation({
          eventNames: ['click', 'submit', 'keydown'],
        }),
      ],
    });

    // eslint-disable-next-line no-console
    console.log(`[otel-web] initialized → ${otlpEndpoint} (auth via proxy)`);
  })();
}
